import { PersonRequestSummaryStatus, VacationRequestColorizeType, VacationRequestPriority, PersonRequestType } from '@app/AppConstants/PersonVacation';
import { ActivityCode } from '@app/AppConstants/ActivityCode';
import DateTimeService from '@app/Services/DateTimeService';
import { ActivityService } from '@app/Services/ActivityService';
import { service } from '@app/Services/_ServicesRegister';
import { IDayDuty } from './IDutyItem';
import { DutyItem, DutySubItem } from '@app/Models/WebApiModels';

export class DayDuty implements IDayDuty {
    private _activityService: ActivityService = service.resolve(ActivityService);
    private _hasDuties: boolean = false;
    private _dutyClass: string = 'person-grid-duty';
    private _dutyDay: Date;
    private _subTitle: string = '';
    private _codeName: string = '';
    private _flightTitle: string = '';
    private _flightDateFrom: Date | null;
    private _flightDateTo: Date | null;
    private _hasPosition: boolean = false;
    private _hasHomeStudy: boolean = false;
    private _hasDutyPart: boolean = false;
    private _duty: DutyItem | null;
    private _isSplitDay: boolean = false;

    constructor(dutyDay: Date, dayDuty: DutyItem | null) {
        this.dutyDay = dutyDay;
        this._duty = dayDuty;
        if (dutyDay.getUTCDate() === 1) this.dutyClass += ' person-grid-period';

        if (!dayDuty) return;

        if (dayDuty.hasFtlErrors) this.dutyClass += ' has-ftl-errors';
        if (dayDuty.hasGavErrors) this.dutyClass += ' has-gav-errors';
        if (dayDuty.hasGroundWarnings) this.dutyClass += ' has-gav-errors';
        if (dayDuty.hasEasaErrors) this.dutyClass += ' has-easa-errors';
        if (dayDuty.hasAgreementErrors) this.dutyClass += ' has-agreement-errors';
        if (dayDuty.hasOtherErrors) this.dutyClass += ' has-other-errors';
        if (dayDuty.hasGroundErrors) this.dutyClass += ' has-other-errors';
        if (dayDuty.isNotCountableOff && !dayDuty.isAfterNightDuty && !dayDuty.isBeforeNightDuty) this.dutyClass += ' off-no-count';

        if (dayDuty.hasNoContract) this.dutyClass += ' no-contract';
        if (dayDuty.hasAugmentedFlight) this.dutyClass += ' augmented';
        if (dayDuty.hasExtendedFlight) this.dutyClass += ' extended';
        if (dayDuty.hasLongHaulFlight) this.dutyClass += ' rotation-flight';
        if (!dayDuty.hasLongHaulFlight && dayDuty.activities.some(a => !!a.flightId)) this.dutyClass += ' short-haul-flight';
        if (dayDuty.hasCustomCheckin) this.dutyClass += ' has-custom-ci';
        if (dayDuty.hasCustomCheckout) this.dutyClass += ' has-custom-co';
        if (dayDuty.hasComment) this.dutyClass += ' has-comment';
        if (dayDuty.isFormerStandbyOrReserve) this.dutyClass += ' has-frsv-fsb';
        if (dayDuty.hasNotFullSuppressed) this.dutyClass += ' not-full-suppressed';

        if (!(dayDuty.activities.length || dayDuty.shadowOffType || dayDuty.isBeforeNightDuty || dayDuty.isAfterNightDuty) && dayDuty.requestedItemStatus && dayDuty.requestedItemStatus !== PersonRequestSummaryStatus.Requested) {
            this.dutyClass += ' person-request';
            if (dayDuty.requestedItemPriority === VacationRequestPriority.high) {
                this.dutyClass += '-prio-high';
            }
            if (dayDuty.requestedItemPriority === VacationRequestPriority.medium) {
                this.dutyClass += '-prio-medium';
            }
            if (dayDuty.requestedItemPriority === VacationRequestPriority.low) {
                this.dutyClass += '-prio-low';
            }
            if (dayDuty.requestedItemColorizeType === VacationRequestColorizeType.slashed) {
                this.dutyClass += '-slashed';
            }
            if (dayDuty.requestedItemStatus === PersonRequestSummaryStatus.Approved) {
                this.dutyClass += ' slash-approved';
            }
            if (dayDuty.requestedItemStatus === PersonRequestSummaryStatus.Rejected) {
                this.dutyClass += ' slash-rejected';
            }
            if (dayDuty.requestedItemVacationTypesCount && dayDuty.requestedItemVacationTypesCount > 0) {
                if (dayDuty.requestedItemVacationTypesCount === 1) {
                    this.dutyClass += ' count-one';
                }
                if (dayDuty.requestedItemVacationTypesCount === 2) {
                    this.dutyClass += ' count-two';
                }
                if (dayDuty.requestedItemVacationTypesCount > 2) {
                    this.dutyClass += ' count-many';
                }
            }
        }

        let isSim = false;
        let firstActivity: DutySubItem | null = null;
        let rsvActivity: DutySubItem | null = null;
        const flightActivities: DutySubItem[] = [];
        let nonFlightActivities: DutySubItem[] = [];
        let additionalActivities: DutySubItem[] = [];

        if (dayDuty.checkinDateTimeUtc) {
            this.flightDateFrom = dayDuty.checkinDateTimeUtc;
            this.flightTitle = ' ';
        }

        if (dayDuty.checkoutDateTimeUtc) {
            this.flightDateTo = dayDuty.checkoutDateTimeUtc;
            this.flightTitle = ' ';
        }
        const activities = dayDuty.activities;
        if (activities && activities.length) {
            this._hasDuties = false;

            for (let i = 0; i < activities.length; i++) {
                const activity = activities[i];

                if (activity.activityCodeName === ActivityCode.POS) {
                    this._hasPosition = true;
                }

                if (activity.activityCodeName === ActivityCode.SIM) {
                    isSim = true;
                }

                if (activity.activityCodeName === ActivityCode.RSV) {
                    rsvActivity = activity;
                }

                if (activity.activityCodeName === ActivityCode.HOME_STUDY) {
                    this._hasHomeStudy = true;
                }

                if (activity.activityCodeName === ActivityCode.DUTY_PART) {
                    this._hasDutyPart = true;
                }

                if (ActivityCode.HiddenActivities.indexOf(activity.activityCodeName) !== -1) {
                    continue;
                }
                this._hasDuties = true;

                if (!firstActivity) {
                    firstActivity = activity;
                }

                if (firstActivity && (
                    (activity.dateStartUtc && firstActivity.dateStartUtc && activity.dateStartUtc < firstActivity.dateStartUtc && activity.activityCodeName !== 'ILL' && activity.activityCodeName !== 'ACC')
                    || (firstActivity.activityCodeName === ActivityCode.POS && activity.activityCodeName !== ActivityCode.POS))
                ) {
                    firstActivity = activity;
                }

                if (firstActivity && (firstActivity.activityCodeName === ActivityCode.UFT_F || firstActivity.activityCodeName === ActivityCode.UFT_O)
                    && (activity.activityCodeName !== ActivityCode.SBS || activity.activityCodeName !== ActivityCode.SBC || activity.activityCodeName !== ActivityCode.SBL)
                ) {
                    firstActivity = activity;
                }

                if ((firstActivity.activityCodeName === ActivityCode.ILL || firstActivity.activityCodeName === ActivityCode.ACC) && activity.activityCodeName === ActivityCode.OFC) {
                    firstActivity = activity;
                }

                if (activity.flightId) {
                    flightActivities.push(activity);
                } else if (activity.activityCodeName === ActivityCode.UFT_F || activity.activityCodeName === ActivityCode.UFT_O) {
                    additionalActivities.push(activity);
                } else {
                    nonFlightActivities.push(activity);
                }
            }

            if (firstActivity) {
                this.codeName = firstActivity.activityCodeName;

                if (this.codeName === ActivityCode.OPS && firstActivity.title === `NIT`) {
                    this.codeName = `${ActivityCode.OPS}-NIT`;
                }

                let title = '';
                if (flightActivities.length) {
                    title = this._getFlightsTitle(flightActivities);
                    nonFlightActivities = nonFlightActivities.filter(x => x.activityCodeName !== ActivityCode.POS);
                    if (nonFlightActivities.length) {
                        this.codeName = nonFlightActivities[0].activityCodeName;
                    }
                }

                nonFlightActivities.push(...additionalActivities);
                if (nonFlightActivities.length) {
                    if (!dayDuty.isAfterNightDuty && !dayDuty.isBeforeNightDuty) {
                        const dutyTitle = nonFlightActivities.map(a => {
                            return this._activityService.buildActivityName(a.activityCodeName, a.vacationType || null) + (a.title && !dayDuty.isFlex ? '-' + a.title : '');
                        }).join('-');
                        if (dutyTitle) title += (title ? '-' : '') + dutyTitle;
                    }

                    let activity = nonFlightActivities[0];
                    if (activity.activityCodeName === ActivityCode.ILL || activity.activityCodeName === ActivityCode.ACC) {
                        for (let i = 0; i < nonFlightActivities.length; i++) {
                            if (nonFlightActivities[i].activityCodeName === ActivityCode.OFC)
                                activity = nonFlightActivities[i];                            
                        }
                    }

                    if (!this.flightDateFrom) {
                        this.flightDateFrom = activity.dateStartUtc && DateTimeService.isValidDate(activity.dateStartUtc) ? activity.dateStartUtc : null;
                    }
                    if (!this.flightDateTo) {
                        this.flightDateTo = activity.dateEndUtc && DateTimeService.isValidDate(activity.dateEndUtc) ? activity.dateEndUtc : null;
                    }
                }

                this.flightTitle = title;
            }
        }

        if (!firstActivity && dayDuty.checkinDateTimeUtc && dayDuty.checkoutDateTimeUtc) {
            this.dutyClass += ' day-split';
            this._isSplitDay = true;
        }

        if (dayDuty.isAfterNightDuty || dayDuty.isBeforeNightDuty) {
            const codeName = dayDuty.isAfterNightDuty ? ActivityCode.AND : ActivityCode.BND;
            if (flightActivities.length) {
                if (codeName === ActivityCode.AND) {
                    this.flightTitle = this.flightTitle + '-' + codeName;
                    this.codeName = codeName;
                } else {
                    this.codeName = this.flightTitle = this.flightTitle + '-' + codeName;
                }
            } else {
                this.codeName = this.flightTitle = codeName;
            }
        }

        this._hasDuties = this._hasDuties || this._hasPosition;

        if (dayDuty.customTitle) {
            const isSpecialRsv = rsvActivity && rsvActivity.dateStartUtc && rsvActivity.dateEndUtc;
            if (isSim || isSpecialRsv) {
                this.flightTitle += '-' + dayDuty.customTitle;
            } else if (dayDuty.isAfterNightDuty) {
                this.subTitle = '';
            } else {
                this.subTitle = dayDuty.customTitle;
            }
        }
    }

    private _getFlightsTitle(flightActivities: DutySubItem[]) {
        let flightActivity: DutySubItem | null = null;
        for (const a of flightActivities) {
            if (a.flightId) {
                flightActivity = a;
                break;
            }
        }

        if (!flightActivity) {
            return '';
        }

        const isDifferentRotationsFlights = flightActivities.length === 2 && flightActivity.rotationKey !== flightActivities[flightActivities.length - 1].rotationKey;
        if (flightActivities.length > 2 && flightActivity.flightNumber || isDifferentRotationsFlights) {
            const lastFlightActivity = flightActivities[flightActivities.length - 1];
            return flightActivity.flightNumber + '-' + lastFlightActivity.flightNumber;
        } else {
            const flightNumber: number | null = flightActivity.flightNumber || null;
            return flightNumber ? flightNumber.toString() : '';
        }
    }

    get dutyDay(): Date {
        //1995-12-17T03:24:00Z
        //https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date
        return this._dutyDay;
    }

    set dutyDay(value: Date) {
        this._dutyDay = value;
    }

    get dutyClass(): string {
        return this._dutyClass;
    }

    set dutyClass(value: string) {
        this._dutyClass = value;
    }

    get codeName(): string {
        return this._codeName;
    }

    set codeName(value: string) {
        this._codeName = value;
    }

    get subTitle(): string {
        return this._subTitle;
    }

    set subTitle(value: string) {
        this._subTitle = value;
    }

    get flightTitle(): string {
        return this._flightTitle;
    }

    set flightTitle(value: string) {
        this._flightTitle = value;
    }

    get flightDateFrom(): Date | null {
        return this._flightDateFrom;
    }

    set flightDateFrom(value: Date | null) {
        this._flightDateFrom = value;
    }

    get flightDateTo(): Date | null {
        return this._flightDateTo;
    }

    set flightDateTo(value: Date | null) {
        this._flightDateTo = value;
    }

    get hasDuties(): boolean {
        return this._hasDuties;
    }

    set hasDuties(value: boolean) {
        this._hasDuties = value;
    }

    get hasPosition(): boolean {
        return this._hasPosition;
    }

    set hasPosition(value: boolean) {
        this._hasPosition = value;
    }

    get isSplitDay(): boolean {
        return this._isSplitDay;
    }

    set isSplitDay(value: boolean) {
        this._isSplitDay = value;
    }

    get hasHomeStudy(): boolean {
        return this._hasHomeStudy;
    }

    set hasHomeStudy(value: boolean) {
        this._hasHomeStudy = value;
    }

    get hasDutyPart() {
        return this._hasDutyPart;
    }

    set hasDutyPart(value: boolean) {
        this._hasDutyPart = value;
    }

    get title() {
        let title = this.flightTitle;
        const duty = this._duty;

        if (duty) {
            let shadowOffTitle = '';
            if (duty.shadowDutyType) {
                if (duty.shadowDutyType === 'Off') {
                    shadowOffTitle = 'OFF';
                }
                if (duty.shadowDutyType === 'Lay') {
                    shadowOffTitle = 'LAY';

                    if (duty.shadowOffType === 'LayT') {
                        shadowOffTitle = 'LAY-T';
                    }
                }
            }

            if (!duty.isAfterNightDuty && !duty.isBeforeNightDuty && duty.requestedItemStatus && duty.requestedItemStatus !== PersonRequestSummaryStatus.Requested && !this.hasDuties) {
                if (duty.requestedItemType === PersonRequestType.Flight) {
                    if (duty.requestedActivity) {
                        title = duty.requestedActivity.toUpperCase();
                    }
                    if (duty.requestedFlightNumber) {
                        title = duty.requestedFlightNumber;
                    }
                }
                if (duty.requestedItemType === PersonRequestType.Vacation) {
                    title += duty.requestedItemVacationType && duty.requestedItemVacationType.toUpperCase()
                        || duty.requestedActivity && duty.requestedActivity.toUpperCase()
                        || (duty.requestedItemIsAlternative ? 'VAC_V' : duty.requestedItemVacationType)
                        || '';
                }
                if (duty.requestedItemType === PersonRequestType.LTO) {
                    title = 'LTO';
                }
                if (duty.requestedItemType === PersonRequestType.LTE) {
                    title = 'LTE';
                }
                if (duty.requestedItemType === PersonRequestType.STO) {
                    title = 'STO';
                }
                if (duty.requestedItemType === PersonRequestType.Reserve) {
                    title = ActivityCode.RSV_P;
                }
            }

            if (title) return title;

            return shadowOffTitle;
        }

        return title;
    }

    get needWrapTitle(): boolean {
        const titleIsLong = this.flightTitle.length > 10;
        return titleIsLong || !(this._duty && this._duty.requestedItemVacationTypesCount && this._duty.requestedItemVacationTypesCount > 0);
    }

    get isPlanningPeriodHidden(): boolean {
        return !!this._duty?.isPlanningPeriodHidden;
    }
}
